import React, { useEffect, useRef, useState } from "react";
import { C, font, mono, inp, lbl, sel, btnP, btnS } from "../../../lib/styles.js";
import { computeLidarLineItems, normalizeLidarBlocks, lidarEstimatorDefaults, lidarOvertimeFromHours, lidarBlockName, lidarBlockInserted, cleanGroupLabel, cleanGroupNote } from "../../../lib/builderHelpers.js";
import { LIDAR_MODEL } from "../../../lib/constants.js";
import { fmtMoney } from "../../../lib/utils.js";

/*
  LidarEstimator — Quote Builder widget for terrestrial LiDAR (RTC 360).
  Captures the operator's inputs, live-previews the derived price via the shared
  computeLidarLineItems() calculator (single source of truth = LIDAR_MODEL), and on
  Insert appends the computed line items to the quote: Capture / Processing / Rush /
  Overtime, each its own line. Every field the price depends on is here, so the
  resulting lines are specific + traceable (see the `lidar` params they carry).

  Modes: capture + processing, capture only (nothing delivered), processing only (data
  already captured — a deferred-processing change order). Days multiply the per-day
  lines (capture, overtime); setups are a job total.

  Blocks (2026-09-03): a brief can carry several LiDAR scopes priced on their own (the
  9/12 job had four on three sites). `initial` is the quote's `lidarPrefill`, a list of
  blocks; the widget opens on the first block the quote does not carry yet, shows the
  others as chips, and Insert appends that block's lines then moves to the next pending
  block, closing after the last. Every block has a label that prefixes its lines and
  names their group on the PDF, and an optional heading note. A single unlabeled block
  behaves exactly as the estimator always has.

  Prefill: fields the brief stated open pre-filled and carry a "brief" tag whose hover
  shows the phrase the model read them from; everything else falls back to
  lidarEstimatorDefaults(). The AI never sets a price: the calculator prices the
  prefilled inputs exactly as it would hand-typed ones.

  Target (2026-09-04): when the builder's "Adding to" block is set and the brief has no
  block left to walk, `target` ({ label, note }) seeds the Block and Heading note fields
  from that block instead of the brief, and re-seeds just those two fields when the
  target changes while the widget is open (crew, setups and the rest keep what was
  typed). Nothing is tagged, no chips show, and Insert reports no block index, so the
  brief's prefill is never written to. Both fields stay editable: a different name
  creates or joins a different block.
*/

const MODES = [["processing", "Capture + processing"], ["captureOnly", "Capture only"], ["processingOnly", "Processing only"]];
const DAY_TYPES = [["standard", `Standard (${LIDAR_MODEL.capture.dayHours.standard} hr)`], ["film", `Film & TV (${LIDAR_MODEL.capture.dayHours.film} hr)`]];

const isWhole = (v, min, max) => {
  if (v === null || v === undefined || String(v).trim() === "") return false;
  const n = Number(v);
  return Number.isInteger(n) && n >= min && n <= max;
};

export default function LidarEstimator({ onInsert, onClose, initial = null, industry = "other", lineItems = [], target = null }) {
  const M = LIDAR_MODEL;
  // Sanitized once at mount; the widget is keyed on quote id + prefill upstream, so a
  // new extraction remounts it with the new blocks. [] when opened blank from the button.
  const [blocks, setBlocks] = useState(() => normalizeLidarBlocks(initial) || []);
  // Blocks inserted from THIS widget, by index. A block is also "inserted" when the quote
  // already carries a LiDAR line stamped with its name (a reopened draft), but a label the
  // operator edited before inserting would not match, so the local set is kept as well.
  const [done, setDone] = useState(() => new Set());
  const isDone = (i) => done.has(i) || lidarBlockInserted(blocks[i], i, lineItems);
  const firstPending = blocks.findIndex((_, i) => !isDone(i));
  const [active, setActive] = useState(firstPending >= 0 ? firstPending : 0);
  const prefill = blocks[active] || {};
  const fromBrief = (k) => Object.prototype.hasOwnProperty.call(prefill, k);
  const evidence = (k) => prefill.evidence?.[k];

  const [form, setForm] = useState(() => ({
    ...lidarEstimatorDefaults(prefill, industry),
    ...(target && !blocks.length ? { label: cleanGroupLabel(target.label), note: cleanGroupNote(target.note) } : {}),
  }));
  // Once the operator types an overtime figure it is theirs: flipping Standard/Film no
  // longer re-derives it from the brief's hours on site (GO review, 2026-09-03).
  const [otTouched, setOtTouched] = useState(false);
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  // "Adding to" changed while open: only the Block and Heading note follow it.
  const targetLabel = target ? cleanGroupLabel(target.label) : null;
  const targetNote = target ? cleanGroupNote(target.note) : null;
  // Switching the pill to "No block" while open clears the two fields again, so the next
  // Insert cannot land in a block the operator just left (GO review, 2026-09-04).
  const prevTarget = useRef(targetLabel);
  useEffect(() => {
    if (blocks.length) return;
    const had = prevTarget.current != null;
    prevTarget.current = targetLabel;
    if (targetLabel != null) setForm((f) => ({ ...f, label: targetLabel, note: targetNote }));
    else if (had) setForm((f) => ({ ...f, label: "", note: "" }));
  }, [targetLabel, targetNote, blocks.length]);
  const openBlock = (i) => {
    setActive(i);
    setForm(lidarEstimatorDefaults(blocks[i] || {}, industry));
    setOtTouched(false);
  };

  // Overtime implied by stated hours on site for the day type being shown; only while
  // the brief gave hours, gave no overtime figure, and the operator has not typed one.
  const impliedOvertime = (dayType) =>
    (prefill.hoursOnSite != null && !fromBrief("overtimeHrs") && !otTouched) ? lidarOvertimeFromHours(prefill.hoursOnSite, dayType) : null;
  const setDayType = (dayType) => {
    const ot = impliedOvertime(dayType);
    upd({ dayType, ...(ot != null && form.timeBand === "full" ? { overtimeHrs: ot } : {}) });
  };

  const isProcessingOnly = form.mode === "processingOnly";
  const hasCapture = !isProcessingOnly;
  const hasProcessing = form.mode !== "captureOnly";
  const isPartial = form.timeBand === "partial";  // overtime can't happen on a 0-3h day
  const dayHours = M.capture.dayHours[form.dayType === "film" ? "film" : "standard"];

  // With several blocks a label is the block's identity, so an emptied label field falls
  // back to the block's name rather than emitting unstamped lines nobody can track.
  const multi = blocks.length > 1;
  const label = cleanGroupLabel(form.label) || (multi ? lidarBlockName(prefill, active) : "");
  const note = cleanGroupNote(form.note);
  const result = computeLidarLineItems({ ...form, label });
  const items = result.items || [];
  // Whole-number guards: the calculator would otherwise round 1.5 crew to 2 or clamp 13 to
  // 12 and price something the box does not show (GO review, 2026-09-03). Processing-only
  // has no crew or days on site to validate.
  const crewOk = !hasCapture || isWhole(form.crewSize, 1, 12);
  const daysOk = !hasCapture || isWhole(form.days, 1, 30);
  const setupsOk = !hasProcessing || isWhole(form.setupCount, 0, 100000);
  const err = result.error
    || (!crewOk ? "Enter the crew on site as a whole number from 1 to 12." : undefined)
    || (!daysOk ? "Enter the capture days as a whole number from 1 to 30." : undefined)
    || (!setupsOk ? "Enter the setup count as a whole number." : undefined);
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);
  const includedUpTo = items.find((it) => it.lidar?.kind === "processing")?.lidar?.includedUpTo;
  const turnaroundDays = form.rush ? M.rush.turnaroundDays : Math.max(1, Math.round(Number(form.processingDays) || M.processing.defaultDays));
  const days = Math.max(1, Math.round(Number(form.days) || 1));

  // Turning photogrammetry on implies a second shooter unless the brief fixed the crew.
  const setPhotogrammetry = (on) => upd({
    photogrammetry: on,
    ...(on && !fromBrief("crewSize") && (Number(form.crewSize) || 0) < 2 ? { crewSize: 2 } : {}),
  });

  // Where Insert goes next: the first pending block after this one, else any pending
  // block before it, else nowhere (close). Skip follows the same path without inserting.
  const pendingOthers = blocks.map((_, i) => i).filter((i) => i !== active && !isDone(i));
  const next = pendingOthers.find((i) => i > active) ?? pendingOthers[0];
  const hasNext = next !== undefined;

  // blockIndex lets the builder write an edited label back onto the block, so a renamed
  // block is still found inserted after this widget closes (Codex review, 2026-09-03).
  const insert = () => {
    if (err || !items.length) return;
    onInsert(items, { label, note, blockIndex: blocks.length ? active : null });
    // The chips and the inserted check follow the name the lines were stamped with.
    if (blocks.length) setBlocks((prev) => prev.map((b, i) => (i === active ? { ...b, label, note } : b)));
    setDone((prev) => new Set(prev).add(active));
    if (hasNext) openBlock(next); else onClose();
  };
  const skip = () => { if (hasNext) openBlock(next); else onClose(); };

  // A plain render helper, not a component: defining a component inside the render would
  // remount these label nodes on every keystroke (GO review, 2026-09-03).
  const fieldLabel = (k, text) => (
    <label style={lbl}>{text}{fromBrief(k) && <span style={ST.tag} title={evidence(k) ? `From the brief: "${evidence(k)}"` : "Stated in the brief"}>brief</span>}</label>
  );
  const briefTag = (k) => fromBrief(k) && <span style={ST.tag} title={evidence(k) ? `From the brief: "${evidence(k)}"` : "Stated in the brief"}>brief</span>;

  const primaryText = hasNext
    ? `Insert and open block ${next + 1}`
    : `Insert line item${items.length > 1 ? "s" : ""}${multi && isDone(active) ? " again" : ""}`;

  return (
    <div style={ST.wrap} data-testid="lidar-estimator">
      {multi && (
        <div style={ST.chips} role="tablist" aria-label="LiDAR blocks">
          {blocks.map((b, i) => (
            <button key={i} type="button" role="tab" aria-selected={i === active} onClick={() => openBlock(i)}
              style={{ ...ST.chip, ...(i === active ? ST.chipOn : {}) }}>
              <span style={ST.chipN}>{i + 1}</span>{lidarBlockName(b, i)}
              {isDone(i) && <span style={{ ...ST.chipOk, ...(i === active ? ST.chipOkOn : {}) }}>inserted</span>}
            </button>
          ))}
        </div>
      )}
      {Object.keys(prefill).length > 0 && (
        <div style={ST.briefNote}>Pre-filled from the brief. Fields marked <span style={{ ...ST.tag, marginLeft: 0 }}>brief</span> came from the email or your notes; hover a tag to see the words it came from, and check everything before inserting.</div>
      )}

      <div style={ST.grid}>
        <div style={{ ...ST.field, gridColumn: "1 / -1" }}>
          {fieldLabel("label", "Block")}
          <input type="text" maxLength={60} style={ST.text} value={form.label || ""} placeholder="Names this scope on every line and as its heading, for example Pile. Leave empty for a single scope."
            onChange={(e) => upd({ label: e.target.value })} />
          {target && !blocks.length && label && <span style={ST.cap}>Lines will land in the {label} block.</span>}
        </div>
        {(form.label || "").trim() !== "" && (
          <div style={{ ...ST.field, gridColumn: "1 / -1" }}>
            {fieldLabel("note", "Heading note")}
            <input type="text" maxLength={120} style={ST.text} value={form.note || ""} placeholder="Optional line under the heading, for example the date or the place."
              onChange={(e) => upd({ note: e.target.value })} />
          </div>
        )}

        <div style={ST.field}>
          {fieldLabel("mode", "Mode")}
          <div style={ST.radios}>
            {MODES.map(([v, text]) => (
              <button key={v} type="button" onClick={() => upd({ mode: v })}
                style={{ ...ST.pill, ...(form.mode === v ? ST.pillOn : {}) }}>{text}</button>
            ))}
          </div>
        </div>

        {hasCapture && (
          <div style={ST.field}>
            {fieldLabel("dayType", "Day type")}
            <div style={ST.radios}>
              {DAY_TYPES.map(([v, text]) => (
                <button key={v} type="button" onClick={() => setDayType(v)}
                  style={{ ...ST.pill, ...(form.dayType === v ? ST.pillOn : {}) }}>{text}</button>
              ))}
            </div>
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            {fieldLabel("timeBand", "Time on site")}
            <select style={ST.selField} value={form.timeBand} onChange={(e) => upd({ timeBand: e.target.value, ...(e.target.value === "partial" ? { overtimeHrs: 0 } : {}) })}>
              <option value="partial">Partial day (0–{M.capture.partialHours} h)</option>
              <option value="full">Full day ({dayHours} h)</option>
            </select>
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            {fieldLabel("crewSize", "Crew on site")}
            <input type="number" min="1" max="12" step="1" style={ST.num} value={form.crewSize}
              onChange={(e) => upd({ crewSize: e.target.value })} />
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            {fieldLabel("days", "Capture days")}
            <input type="number" min="1" max="30" step="1" style={ST.num} value={form.days}
              onChange={(e) => upd({ days: e.target.value })} />
          </div>
        )}

        <div style={ST.field}>
          {fieldLabel("setupCount", hasProcessing ? "Number of setups" : "Setups (recorded, not priced)")}
          <input type="number" min="0" step="1" style={ST.num} value={form.setupCount}
            onChange={(e) => upd({ setupCount: e.target.value })} />
        </div>

        {hasProcessing && (
          <div style={ST.field}>
            {fieldLabel("processingDays", `Processing turnaround (biz days)${form.rush ? " — rush" : ""}`)}
            <input type="number" min="1" disabled={form.rush}
              style={{ ...ST.num, ...(form.rush ? ST.numDisabled : {}) }}
              value={form.rush ? M.rush.turnaroundDays : form.processingDays}
              onChange={(e) => upd({ processingDays: e.target.value })} />
          </div>
        )}

        {hasCapture && (
          <div style={ST.field}>
            {fieldLabel("overtimeHrs", `Overtime hrs (past ${dayHours})${isPartial ? " — full day only" : ""}${days > 1 ? " · per day" : ""}`)}
            <input type="number" min="0" step="0.5" disabled={isPartial}
              style={{ ...ST.num, ...(isPartial ? ST.numDisabled : {}) }}
              value={isPartial ? 0 : form.overtimeHrs}
              onChange={(e) => { setOtTouched(true); upd({ overtimeHrs: e.target.value }); }} />
          </div>
        )}
      </div>

      <div style={ST.checks}>
        <label style={ST.check}>
          <input type="checkbox" checked={form.photogrammetry} onChange={(e) => setPhotogrammetry(e.target.checked)} />
          {hasCapture ? "+ Photogrammetry (Canon R5 texture pass; usually a second shooter)" : "+ Photogrammetry (texture the OBJ from Canon R5 photos)"}{briefTag("photogrammetry")}
        </label>
        {hasProcessing && (
          <label style={ST.check}>
            <input type="checkbox" checked={form.rush} onChange={(e) => upd({ rush: e.target.checked })} />
            Rush processing (starts right after capture, {M.rush.turnaroundDays} business day turnaround; {Math.round(M.rush.processingMultiplier * 100)}% of processing{M.rush.minimum > 0 ? `, ${fmtMoney(M.rush.minimum)} minimum` : ""}){briefTag("rush")}
          </label>
        )}
        {hasProcessing && (
          <label style={{ ...ST.check, color: C.muted }}>
            <input type="checkbox" checked={form.highDensity} onChange={(e) => upd({ highDensity: e.target.checked })} />
            High-density scans (processing ×{M.highDensity.processingMultiplier}){briefTag("highDensity")}
          </label>
        )}
      </div>

      {err ? (
        <div style={ST.err}>{err}</div>
      ) : (
        <div style={ST.preview}>
          {items.map((it, i) => (
            <div key={i} style={ST.liRow}>
              <div>
                <div style={ST.liDesc}>{it.description}{it.qty > 1 ? <span style={ST.liQty}> × {it.qty}</span> : null}</div>
                <div style={ST.liNotes}>{it.notes}</div>
              </div>
              <div style={ST.liRate}>{fmtMoney(it.rate * (it.qty || 1))}</div>
            </div>
          ))}
          <div style={ST.total}><span>Total</span><span style={{ fontFamily: mono }}>{fmtMoney(total)}</span></div>
          {hasProcessing && (
            <div style={ST.procNote}>
              Processing turnaround: ~{turnaroundDays} business day{turnaroundDays === 1 ? "" : "s"} from {isProcessingOnly ? "receipt of the scans" : "capture"}
              {includedUpTo ? ` · scan set of up to ${includedUpTo} positions included` : ""}
            </div>
          )}
        </div>
      )}

      <div style={ST.foot}>
        <button type="button" style={btnS} onClick={onClose}>Cancel</button>
        {multi && hasNext && <button type="button" style={btnS} onClick={skip}>Skip block</button>}
        <button type="button" style={{ ...btnP, opacity: err || !items.length ? 0.4 : 1, cursor: err ? "not-allowed" : "pointer" }}
          disabled={!!err || !items.length} onClick={insert}>{primaryText}</button>
      </div>
    </div>
  );
}

const ST = {
  wrap: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, marginBottom: 12, display: "grid", gap: 12 },
  chips: { display: "flex", gap: 6, flexWrap: "wrap" },
  chip: { ...btnS, display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px", fontSize: 12, background: C.surface },
  chipOn: { background: C.text, color: "#FFF", borderColor: C.text },
  chipN: { fontFamily: mono, fontSize: 10, opacity: 0.7 },
  chipOk: { color: C.green, fontWeight: 500, fontSize: 11 },
  chipOkOn: { color: "#9FE8CF" },
  briefNote: { fontSize: 12.5, color: C.text, background: "#FBF6EA", border: `1px solid ${C.amber}`, borderRadius: 6, padding: "8px 12px" },
  cap: { fontSize: 12, color: C.muted, marginTop: 2 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 },
  field: { display: "grid", gap: 4 },
  radios: { display: "flex", gap: 6, flexWrap: "wrap" },
  pill: { ...btnS, padding: "8px 12px", fontSize: 12 },
  pillOn: { background: C.text, color: "#FFF", borderColor: C.text },
  num: { ...inp, background: C.surface, fontWeight: 400 },
  text: { ...inp, background: C.surface, fontWeight: 400 },
  numDisabled: { opacity: 0.45, cursor: "not-allowed" },
  selField: { ...sel, backgroundColor: C.surface },
  checks: { display: "grid", gap: 8 },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: C.text, cursor: "pointer", fontFamily: font },
  tag: { display: "inline-block", marginLeft: 6, padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", background: C.amber, color: "#FFF", verticalAlign: "middle", cursor: "help" },
  preview: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "12px 14px", display: "grid", gap: 8 },
  liRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" },
  liDesc: { color: C.text, fontSize: 12.5, fontWeight: 400 },
  liQty: { color: C.muted, fontFamily: mono, fontSize: 12 },
  liNotes: { color: C.muted, fontSize: 11.5, marginTop: 2 },
  liRate: { fontFamily: mono, color: C.text, fontSize: 13, whiteSpace: "nowrap" },
  total: { display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, paddingTop: 8, fontWeight: 500, fontSize: 13.5, color: C.text },
  procNote: { fontSize: 12, color: C.muted, marginTop: 4 },
  err: { color: C.danger, fontSize: 12.5, background: `${C.danger}10`, border: `1px solid ${C.danger}33`, borderRadius: 6, padding: "10px 12px" },
  foot: { display: "flex", justifyContent: "flex-end", gap: 8 },
};
